import { useCallback } from 'react'

import type { IconColorName, IconName } from '@buffer-mono/popcorn'
import { toast } from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { useSavedViewTracking } from '~publish/hooks/useSavedViewTracking'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { SavedView } from './useSavedViewsQuery'
import { missingOrganizationOutcome } from './utils'
import { SavedViewsQuery } from '../graphql/SavedViewsQuery'
import { UpdateSavedViewMutation } from '../graphql/UpdateSavedViewMutation'

type UpdateSavedViewArgs = {
  id: SavedView['id']
  name?: string
  view?: string | null
  filters?: Record<string, unknown>
  icon?: IconName
  color?: IconColorName
  optimistic?: SavedView
}

type UpdateSavedViewMutationResult = ReturnType<
  typeof useTypedMutation<
    'SavedView',
    ResultOf<typeof UpdateSavedViewMutation>['updateSavedView'],
    ResultOf<typeof UpdateSavedViewMutation>,
    VariablesOf<typeof UpdateSavedViewMutation>
  >
>

type UseUpdateSavedViewMutationResult = readonly [
  (
    args: UpdateSavedViewArgs,
  ) => Promise<Awaited<ReturnType<UpdateSavedViewMutationResult[0]>>>,
  UpdateSavedViewMutationResult[1],
]

/**
 * Update a saved view with optimistic update and success/error toasts.
 * Apollo auto-reverts the optimistic layer if the mutation fails.
 */
export function useUpdateSavedViewMutation(): UseUpdateSavedViewMutationResult {
  const organizationId = useCurrentOrganization()?.id
  const { trackUpdated } = useSavedViewTracking()

  const [mutate, result] = useTypedMutation(
    UpdateSavedViewMutation,
    (data) => data.updateSavedView,
    {
      successTypename: 'SavedView',
      refetchQueries: [SavedViewsQuery],
      awaitRefetchQueries: true,
    },
  )

  const updateSavedView = useCallback(
    async ({
      id,
      name,
      view,
      filters,
      icon,
      color,
      optimistic,
    }: UpdateSavedViewArgs) => {
      if (!organizationId) return missingOrganizationOutcome()

      const nowIso = new Date().toISOString()
      const optimisticResponse = optimistic
        ? {
            updateSavedView: {
              __typename: 'SavedView' as const,
              id,
              name: name ?? optimistic.name,
              section: optimistic.section,
              view: view !== undefined ? view : optimistic.view,
              filters: filters ?? optimistic.filters,
              icon: icon !== undefined ? icon : (optimistic.icon ?? null),
              color: color !== undefined ? color : (optimistic.color ?? null),
              createdAt: optimistic.createdAt,
              updatedAt: nowIso,
            },
          }
        : undefined

      const outcome = await mutate({
        variables: {
          input: {
            id,
            organizationId,
            ...(name !== undefined ? { name } : {}),
            ...(view !== undefined ? { view } : {}),
            ...(filters !== undefined ? { filters } : {}),
            ...(icon !== undefined ? { icon } : {}),
            ...(color !== undefined ? { color } : {}),
          },
        },
        ...(optimisticResponse ? { optimisticResponse } : {}),
      })

      if (outcome.success) {
        toast.success('View updated')
        // Derive `updateType` by diffing against the optimistic snapshot, not
        // by field presence: the rename dialog always sends name+icon+color,
        // so a presence check would tag every rename `multiple`. When no
        // snapshot is available we fall back to presence (a defined field is
        // treated as changed). `multiple` covers more than one field moving in
        // the same mutation.
        const nameChanged =
          name !== undefined && (optimistic ? name !== optimistic.name : true)
        const identityChanged =
          (icon !== undefined && icon !== optimistic?.icon) ||
          (color !== undefined && color !== optimistic?.color)
        const changed = [
          nameChanged ? 'rename' : null,
          filters !== undefined ? 'filters' : null,
          view !== undefined ? 'view' : null,
          identityChanged ? 'icon' : null,
        ].filter((c): c is 'rename' | 'filters' | 'view' | 'icon' => c !== null)
        const updateType =
          changed.length === 0
            ? undefined
            : changed.length > 1
              ? 'multiple'
              : changed[0]

        // Fire after server confirmation so the tracked section/view
        // reflect the persisted state, not the optimistic snapshot.
        trackUpdated(
          {
            id: outcome.data.id,
            section: outcome.data.section,
            view: outcome.data.view,
          },
          updateType,
        )
      } else {
        toast.error(outcome.error.message)
      }

      return outcome
    },
    [mutate, organizationId, trackUpdated],
  )

  return [updateSavedView, result] as const
}
